import { createAction } from '@reduxjs/toolkit';
import type { IProfile } from '../types/authProfile';
export type AvailableAction = { code: number; name: string };
export type SessionProfile = IProfile & { userId: string; id?: string; fullName?: string };
export type MyselfResponse = { profile: SessionProfile; platformRoles: string[]; availableActions: AvailableAction[] };
export type AccessStatus = 'loading' | 'ready' | 'refreshing' | 'error' | 'anonymous';
export type AccessState = {
  profile: SessionProfile | null; platformRoles: string[]; availableActions: AvailableAction[];
  accessStatus: AccessStatus; generation: number; accessRequestId?: string;
};
export type AccessRoot = { auth?: AccessState };
export const refreshAccess = createAction('access/refreshRequested');
export const clearAdminData = createAction('access/clearAdminData');
export const legacyFilerAdminOnly = 'legacyFilerAdminOnly';
export type Requirement = string | readonly string[];
export const validProjection = (value: unknown): value is MyselfResponse => {
  const p = value as MyselfResponse | null;
  return !!p && typeof p.profile?.userId === 'string' && !!p.profile.userId &&
    typeof p.profile.email === 'string' &&
    Array.isArray(p.platformRoles) && p.platformRoles.every(r => typeof r === 'string') &&
    Array.isArray(p.availableActions) && p.availableActions.every(a =>
      !!a && Number.isInteger(a.code) && typeof a.name === 'string');
};
export const permits = (state: AccessRoot, requirement: Requirement): boolean => {
  const auth = state.auth;
  // A background refresh keeps the last valid projection for UI and reads.
  // BaseService queues writes until myself resolves and rechecks their identity.
  // The server still authorizes every request; failed/expired sessions deny.
  if (!auth || !(auth.accessStatus === 'ready' || auth.accessStatus === 'refreshing')) return false;
  return (typeof requirement === 'string' ? [requirement] : requirement).some(action => {
    // Owner decision 2026-09-08: only filer tags/translations retain RolesGuard(ADMIN).
    // They have no actions in myself; backend ADMIN_TAG_* are a different API.
    if (action === legacyFilerAdminOnly) return auth.platformRoles.includes('ADMIN');
    return auth.availableActions.some(entry => entry.name === action);
  });
};
export const displayName = (profile: { fullName?: string; email?: string } | null) => {
  const name = profile?.fullName?.trim();
  return name && name !== 'undefined undefined' ? name : profile?.email || '';
};
